import type { PanelMesh, Vec2 } from '../types';
import { offsetConvexPolygon } from '../shapes/tessellate';

/**
 * The light-box frame.
 *
 * A spacer that sits between the wall and the panel, holding the LED strip at a
 * real distance behind it. It is the part that makes a backlit panel actually
 * glow rather than show a row of dots.
 *
 * Unlike every other part in this project, the frame is **not** a height field.
 * A 20 mm wall rising over one mesh sample would be a ramp, not a wall — a
 * height field's steepest possible feature is one sample wide, which is fine
 * for a relief and useless for a box. So this is built directly as an annular
 * prism: two rings, a floor, and quads between them.
 *
 * What the rim height *can* vary with, per boundary vertex, is where the strip
 * needs to leave the box. Dropping the rim to floor level over the width of a
 * run opens a slot the strip passes through — a slot rather than a hole, which
 * keeps the mesh a simple prism and prints without bridging.
 */

export interface FrameSpec {
  /** Distance between the strip and the back of the panel, in millimetres. */
  airGap: number;
  /** Thickness of the perimeter wall, in millimetres. */
  wallThickness: number;
  /** Thickness of the floor the strip sits on, in millimetres. */
  floorThickness: number;
}

export interface BuildFrameOptions {
  spec: FrameSpec;
  /** The panel footprint the frame sits under, in panel space. */
  outline: readonly Vec2[];
  /**
   * Rim height at a point on the outline, in millimetres, measured from the
   * frame's base. Returning the floor thickness opens a slot for the strip.
   */
  rimHeight(point: Vec2, index: number): number;
  /** Subdivisions per outline edge; more gives finer slot edges. */
  segmentsPerEdge: number;
}

export interface FrameBuildResult {
  mesh: PanelMesh['mesh'];
  bounds: PanelMesh['bounds'];
  /** Inner outline of the box, for the strip area and the preview. */
  interior: Vec2[];
}

/** Walks an outline, inserting `segmentsPerEdge` points along each edge. */
function densify(outline: readonly Vec2[], segmentsPerEdge: number): Vec2[] {
  const steps = Math.max(1, Math.round(segmentsPerEdge));
  const points: Vec2[] = [];

  for (let i = 0; i < outline.length; i += 1) {
    const a = outline[i];
    const b = outline[(i + 1) % outline.length];
    for (let s = 0; s < steps; s += 1) {
      const t = s / steps;
      points.push({ x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t });
    }
  }

  return points;
}

/**
 * Builds the frame for one panel, in panel space.
 *
 * Returns `null` when the wall thickness leaves no interior — a 25 mm wall on a
 * 50 mm triangle has nowhere to put a strip, and a caller needs to be told that
 * rather than handed a degenerate solid.
 */
export function buildFrameMesh(options: BuildFrameOptions): FrameBuildResult | null {
  const { spec, outline, rimHeight, segmentsPerEdge } = options;

  const innerOutline = offsetConvexPolygon(outline, spec.wallThickness);
  if (!innerOutline) return null;

  const outer = densify(outline, segmentsPerEdge);
  const inner = densify(innerOutline, segmentsPerEdge);
  const ring = outer.length;
  if (ring !== inner.length || ring < 3) return null;

  const floor = spec.floorThickness;
  const fullHeight = floor + spec.airGap;

  // Layout: [ outer base | outer top | inner floor | inner top | floor centre | base centre ]
  const outerBase = 0;
  const outerTop = ring;
  const innerFloor = ring * 2;
  const innerTop = ring * 3;
  const floorCentre = ring * 4;
  const baseCentre = ring * 4 + 1;

  const positions = new Float32Array((ring * 4 + 2) * 3);

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  let centroidX = 0;
  let centroidY = 0;

  for (let i = 0; i < ring; i += 1) {
    const o = outer[i];
    const n = inner[i];
    // The rim is sampled on the outer ring and shared with the inner one, so a
    // slot cuts cleanly through the whole wall rather than shearing it.
    const height = Math.max(floor, Math.min(fullHeight, rimHeight(o, i)));

    positions[(outerBase + i) * 3] = o.x;
    positions[(outerBase + i) * 3 + 1] = o.y;
    positions[(outerBase + i) * 3 + 2] = 0;

    positions[(outerTop + i) * 3] = o.x;
    positions[(outerTop + i) * 3 + 1] = o.y;
    positions[(outerTop + i) * 3 + 2] = height;

    positions[(innerFloor + i) * 3] = n.x;
    positions[(innerFloor + i) * 3 + 1] = n.y;
    positions[(innerFloor + i) * 3 + 2] = floor;

    positions[(innerTop + i) * 3] = n.x;
    positions[(innerTop + i) * 3 + 1] = n.y;
    positions[(innerTop + i) * 3 + 2] = height;

    if (o.x < minX) minX = o.x;
    if (o.y < minY) minY = o.y;
    if (o.x > maxX) maxX = o.x;
    if (o.y > maxY) maxY = o.y;
    centroidX += n.x;
    centroidY += n.y;
  }

  // The two fans sit on different planes, so each needs its own centre point.
  positions[floorCentre * 3] = centroidX / ring;
  positions[floorCentre * 3 + 1] = centroidY / ring;
  positions[floorCentre * 3 + 2] = floor;

  positions[baseCentre * 3] = centroidX / ring;
  positions[baseCentre * 3 + 1] = centroidY / ring;
  positions[baseCentre * 3 + 2] = 0;

  const indices: number[] = [];
  const quad = (a: number, b: number, c: number, d: number): void => {
    indices.push(a, b, c, a, c, d);
  };

  for (let i = 0; i < ring; i += 1) {
    const j = (i + 1) % ring;

    // Outer wall, facing away from the box.
    quad(outerBase + i, outerBase + j, outerTop + j, outerTop + i);
    // Inner wall, facing into the box.
    quad(innerFloor + j, innerFloor + i, innerTop + i, innerTop + j);
    // The rim between them. Where a slot has dropped the height this becomes
    // the floor of that slot, which is why it needs no special case.
    quad(outerTop + i, outerTop + j, innerTop + j, innerTop + i);
    // Floor, seen from inside the box.
    indices.push(floorCentre, innerFloor + i, innerFloor + j);
    // Base, against the wall, wound the other way so it faces down.
    indices.push(baseCentre, outerBase + j, outerBase + i);
  }

  return {
    mesh: { positions, indices: Uint32Array.from(indices) },
    bounds: { min: [minX, minY, 0], max: [maxX, maxY, fullHeight] },
    interior: innerOutline,
  };
}

/** Floor area inside the box, for the reflector and strip-length notes. */
export function interiorArea(interior: readonly Vec2[]): number {
  let area = 0;
  for (let i = 0; i < interior.length; i += 1) {
    const a = interior[i];
    const b = interior[(i + 1) % interior.length];
    area += a.x * b.y - b.x * a.y;
  }
  return Math.abs(area) / 2;
}
